import { inventoryAgeDays, inventoryAgeLabel } from "../../domain/inventoryFacts";
import { Bot, Send, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useReOS } from "../../domain/store";
import type { ReOSDatabase } from "../../domain/types";
import { Button } from "../ui/button";
import { AssistantRichText } from "./AssistantRichText";
import { loadAssistantSkills, saveAssistantSkills, subscribeToAssistantOpen, type AssistantInvocationContext, type AssistantSkill } from "./assistantBridge";

type AssistantAction = { id: string; kind: string; title: string; summary: string; requiredAction: string; payload: Record<string, unknown> };
export type AssistantRecordTarget = { id: string; kind: "inventory" | "listing" | "order" };
type AssistantRecord = AssistantRecordTarget & { title: string; meta?: string };
type AssistantBlock =
  | { type: "markdown"; content: string }
  | { type: "metrics"; items: Array<{ label: string; value: string; detail?: string }> }
  | { type: "records"; title: string; records: AssistantRecord[] }
  | { type: "checklist"; title: string; items: Array<{ label: string; status: "complete" | "pending" | "attention" }> };
type AssistantReply = {
  text: string;
  blocks?: AssistantBlock[];
  suggestions?: string[];
  citations?: Array<{ label: string; recordIDs: string[] }>;
  action?: AssistantAction;
  usage?: { modelCalls: number; monthlyUsed?: number; monthlyLimit?: number; resetsAt?: string };
};
type AssistantMessage = { id: string; role: "assistant" | "user"; text: string; source?: string; reply?: AssistantReply };
type Automation = { id: string; name: string; prompt: string; schedule: "daily" | "weekly"; enabled: boolean; lastRunAt?: string; lastResult?: string };
type AssistantPanelProps = { onOpenRecord?: (record: AssistantRecordTarget) => void };

export function AssistantPanel({ onOpenRecord }: AssistantPanelProps = {}) {
  const store = useReOS();
  const workspaceID = store.workspace?.id;
  const userID = store.database.currentUserID;
  if (!workspaceID || !userID) return null;
  const scope = [store.persistenceMode, userID, workspaceID].map(encodeURIComponent).join(":");
  // A workspace/account change discards UI state and stale requests, not its saved draft.
  return <WorkspaceAssistantPanel key={scope} store={store} scope={scope} workspaceID={workspaceID} onOpenRecord={onOpenRecord} />;
}

function WorkspaceAssistantPanel({ store, scope, workspaceID, onOpenRecord }: {
  store: ReturnType<typeof useReOS>;
  scope: string;
  workspaceID: string;
  onOpenRecord?: AssistantPanelProps["onOpenRecord"];
}) {
  const [open, setOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [input, setInput] = useState(() => loadAssistantDraft(scope));
  const [storageWarning, setStorageWarning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [automationBusy, setAutomationBusy] = useState(false);
  const [automationSetupOpen, setAutomationSetupOpen] = useState(false);
  const [automationState, setAutomationState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [automationError, setAutomationError] = useState<string>();
  const [automationReload, setAutomationReload] = useState(0);
  const [skillSaved, setSkillSaved] = useState(false);
  const [skillError, setSkillError] = useState<string>();
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [skills, setSkills] = useState<AssistantSkill[]>(() => loadAssistantSkills(workspaceID));
  const [pendingPrompt, setPendingPrompt] = useState<string>();
  const [invocationContext, setInvocationContext] = useState<AssistantInvocationContext>();
  const [messages, setMessages] = useState<AssistantMessage[]>(() => loadAssistantThread(scope));
  const [focusRequest, setFocusRequest] = useState(0);
  const mountedRef = useRef(true);
  const busyRef = useRef(false);
  const automationBusyRef = useRef(false);
  const draftRef = useRef(input);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef(new Map<string, HTMLDivElement>());
  const historyFocusRef = useRef<string>();
  const inputRef = useRef<HTMLInputElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const headingID = useId();
  const optionsID = useId();
  const storageWarningID = useId();
  const apiBase = import.meta.env.VITE_REOS_API_BASE_URL?.trim().replace(/\/$/, "");
  const canUseAPI = store.persistenceMode === "api" && Boolean(apiBase);
  const latestReply = useMemo(() => messages.slice().reverse().find((message) => message.role === "assistant" && message.reply)?.reply, [messages]);
  const latestUserMessage = useMemo(() => messages.slice().reverse().find((message) => message.role === "user")?.text, [messages]);
  const usage = useMemo(() => messages.slice().reverse().find((message) => message.reply?.usage?.monthlyUsed !== undefined)?.reply?.usage, [messages]);
  const dailyReview = useMemo(() => automations.find((automation) => automation.name === "Daily reseller review" && automation.schedule === "daily"), [automations]);
  const suggestions = useMemo(() => Array.from(new Set(latestReply?.suggestions ?? ["Give me today's business summary", "Which stock needs attention?"])).slice(0, 3), [latestReply?.suggestions]);
  const recordIndex = useMemo(() => assistantRecordIndex(store.database, workspaceID, messages), [messages, store.database, workspaceID]);

  function updateInput(value: string) {
    draftRef.current = value;
    setInput(value);
    if (!saveAssistantDraft(scope, value)) setStorageWarning(true);
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => subscribeToAssistantOpen((invocation) => {
    setOpen(true);
    setOptionsOpen(false);
    setInvocationContext(invocation.context);
    setFocusRequest((current) => current + 1);
    if (invocation.prompt) {
      if (invocation.autoSubmit) setPendingPrompt(invocation.prompt);
      else updateInput(invocation.prompt);
    }
  }), [scope]);

  useEffect(() => {
    if (!saveAssistantThread(scope, messages)) setStorageWarning(true);
  }, [messages, scope]);

  useEffect(() => {
    setSkillSaved(false);
    setSkillError(undefined);
  }, [latestUserMessage]);

  useEffect(() => {
    if (open && !optionsOpen) messageEndRef.current?.scrollIntoView({ block: "end", behavior: "auto" });
  }, [busy, messages, open]);

  useEffect(() => {
    if (!open) return;
    if (optionsOpen) {
      optionsRef.current?.focus({ preventScroll: true });
      return;
    }
    if (busy || pendingPrompt) return;
    const timeout = window.setTimeout(() => {
      const target = historyFocusRef.current ? messageRefs.current.get(historyFocusRef.current) : undefined;
      historyFocusRef.current = undefined;
      if (target) {
        target.scrollIntoView({ block: "start", behavior: "auto" });
        target.focus({ preventScroll: true });
      } else inputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [busy, focusRequest, messages.length, open, optionsOpen, pendingPrompt]);

  useEffect(() => {
    if (!open || !pendingPrompt || busy) return;
    const prompt = pendingPrompt;
    setPendingPrompt(undefined);
    void sendMessage(prompt);
  }, [busy, open, pendingPrompt]);

  useEffect(() => {
    if (!open || !optionsOpen || !canUseAPI) return;
    const controller = new AbortController();
    setAutomationState("loading");
    setAutomationError(undefined);
    fetch(apiBase + "/api/workspaces/" + encodeURIComponent(workspaceID) + "/assistant/automations", { credentials: "include", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Daily review settings couldn't be loaded.");
        return response.json() as Promise<{ automations?: Automation[] }>;
      })
      .then((body) => {
        if (controller.signal.aborted || !mountedRef.current) return;
        setAutomations(body.automations ?? []);
        setAutomationState("ready");
      })
      .catch(() => {
        if (controller.signal.aborted || !mountedRef.current) return;
        setAutomationState("error");
        setAutomationError("Daily review settings couldn't be loaded. Try again.");
      });
    return () => controller.abort();
  }, [apiBase, automationReload, canUseAPI, open, optionsOpen, workspaceID]);

  async function sendMessage(value = input, confirm = false, action?: AssistantAction) {
    const message = value.trim();
    if (!message || busyRef.current) return;
    busyRef.current = true;
    const sentDraft = !confirm && message === draftRef.current.trim();
    if (!confirm) {
      if (sentDraft) updateInput("");
      setMessages((current) => [...supersedePendingAssistantActions(current), { id: "user-" + Date.now(), role: "user", text: message }]);
    }
    setOptionsOpen(false);
    setBusy(true);
    try {
      const result = canUseAPI
        ? await requestAssistant(apiBase!, workspaceID, message, confirm, messages, invocationContext, action)
        : { reply: localReply(store.database, message), database: undefined };
      if (!mountedRef.current) return;
      const reply = result.reply;
      setMessages((current) => [
        ...current.map((entry) => action && entry.reply?.action?.id === action.id
          ? { ...entry, reply: { ...entry.reply, action: undefined } }
          : entry),
        { id: "assistant-" + Date.now(), role: "assistant", text: reply.text, source: message, reply }
      ]);
      // Read-only replies already contain their answer and records. Reloading the
      // workspace here keeps the composer busy after the answer has arrived.
      if (confirm) await store.refreshWorkspace();
    } catch (error) {
      if (!mountedRef.current) return;
      if (sentDraft && !draftRef.current) updateInput(value);
      setMessages((current) => [...current, { id: "assistant-error-" + Date.now(), role: "assistant", text: error instanceof Error ? error.message : "The assistant could not complete that request." }]);
    } finally {
      busyRef.current = false;
      if (mountedRef.current) setBusy(false);
    }
  }

  function startNewThread() {
    if (busyRef.current) return;
    setPendingPrompt(undefined);
    setMessages(welcomeMessages());
    setInvocationContext(undefined);
    setOptionsOpen(false);
  }

  function closeAssistant() {
    setOpen(false);
    window.setTimeout(() => {
      if (!mountedRef.current) return;
      const launcher = launcherRef.current;
      const trigger = launcher && window.getComputedStyle(launcher).display !== "none"
        ? launcher
        : document.querySelector<HTMLButtonElement>(".workspace-search-trigger, .workspace-search-icon");
      trigger?.focus();
    }, 0);
  }

  function openRecord(record: AssistantRecordTarget) {
    if (!onOpenRecord) return;
    // The application owns routing, workspace checks and destination focus.
    // Never navigate to a URL supplied by an assistant response.
    onOpenRecord({ kind: record.kind, id: record.id });
    setOpen(false);
  }

  function saveLatestAsSkill() {
    if (!latestUserMessage) return;
    const nextSkill: AssistantSkill = { id: "assistant-skill-" + Date.now(), name: latestUserMessage.length > 42 ? latestUserMessage.slice(0, 39) + "..." : latestUserMessage, prompt: latestUserMessage };
    const next = [nextSkill, ...skills.filter((skill) => skill.prompt !== nextSkill.prompt)].slice(0, 25);
    try {
      saveAssistantSkills(workspaceID, next);
      setSkills(next);
      setSkillSaved(true);
      setSkillError(undefined);
    } catch {
      setSkillError("This prompt couldn't be saved on this device. Try again.");
    }
  }

  async function createAutomation() {
    if (!canUseAPI || automationBusyRef.current || automationState !== "ready") return;
    if (dailyReview) { setAutomationSetupOpen(false); return; }
    automationBusyRef.current = true;
    setAutomationBusy(true);
    setAutomationError(undefined);
    try {
      const response = await fetch(apiBase + "/api/workspaces/" + encodeURIComponent(workspaceID) + "/assistant/automations", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: "Daily reseller review", prompt: "Give me a daily summary of sales, aging stock, and listings needing attention.", schedule: "daily", enabled: true }) });
      if (!response.ok) throw new Error("Daily review couldn't be enabled. Try again.");
      const body = await response.json() as { automation: Automation };
      if (!mountedRef.current) return;
      setAutomations((current) => [...current.filter((automation) => automation.id !== body.automation.id), body.automation]);
      setAutomationSetupOpen(false);
      setMessages((current) => [...current, { id: "automation-" + Date.now(), role: "assistant", text: "Daily reseller review is enabled. It will prepare a grounded summary on the next scheduled run and use at most one included assistant request per run." }]);
    } catch (error) {
      if (mountedRef.current) setAutomationError(error instanceof Error ? error.message : "Daily review couldn't be enabled. Try again.");
    } finally {
      automationBusyRef.current = false;
      if (mountedRef.current) setAutomationBusy(false);
    }
  }

  async function deleteAutomation(automation: Automation) {
    if (!canUseAPI || automationBusyRef.current) return;
    automationBusyRef.current = true;
    setAutomationBusy(true);
    setAutomationError(undefined);
    try {
      const response = await fetch(apiBase + "/api/workspaces/" + encodeURIComponent(workspaceID) + "/assistant/automations", { method: "DELETE", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: automation.id }) });
      if (!response.ok) throw new Error("Daily review couldn't be turned off. Try again.");
      if (!mountedRef.current) return;
      setAutomations((current) => current.filter((candidate) => candidate.id !== automation.id));
      setMessages((current) => [...current, { id: "automation-disabled-" + Date.now(), role: "assistant", text: "Daily reseller review is off. No further scheduled reviews will run." }]);
    } catch (error) {
      if (mountedRef.current) setAutomationError(error instanceof Error ? error.message : "Daily review couldn't be changed. Try again.");
    } finally {
      automationBusyRef.current = false;
      if (mountedRef.current) setAutomationBusy(false);
    }
  }

  return (
    <div className={"assistant-root" + (open ? " assistant-open" : "")}>
      {open && (
        <section className="assistant-panel assistant-panel-focused" aria-labelledby={headingID} data-view={optionsOpen ? "options" : "conversation"} onKeyDown={(event) => {
          if (event.key !== "Escape" || event.defaultPrevented) return;
          event.preventDefault();
          event.stopPropagation();
          if (optionsOpen) setOptionsOpen(false); else closeAssistant();
        }}>
          <header className="assistant-header">
            <div className="assistant-heading"><strong id={headingID}>Listwork assistant</strong></div>
            <div className="assistant-header-actions">
              <Button variant="ghost" size="compact" aria-expanded={optionsOpen} aria-controls={optionsID} onClick={() => setOptionsOpen((current) => !current)}>{optionsOpen ? "Back to chat" : "Options"}</Button>
              <Button variant="ghost" size="icon" aria-label="Close assistant" onClick={closeAssistant}><X size={18} /></Button>
            </div>
          </header>
          <div className="assistant-body">
            {optionsOpen ? (
              <div className="assistant-tools" id={optionsID} ref={optionsRef} role="region" aria-label="Assistant options" tabIndex={-1}>
                <details className="assistant-tool-section">
                  <summary>Suggested questions</summary>
                  <div className="assistant-suggestions">{suggestions.map((suggestion) => <Button key={suggestion} variant="ghost" onClick={() => void sendMessage(suggestion)} disabled={busy}>{suggestion}</Button>)}</div>
                </details>
                <details className="assistant-tool-section">
                  <summary>History</summary>
                  <div className="assistant-history">
                    {messages.some((message) => message.role === "user") ? messages.filter((message) => message.role === "user").slice().reverse().map((message) => <Button variant="ghost" key={message.id} onClick={() => { historyFocusRef.current = message.id; setOptionsOpen(false); }}>{message.text}</Button>) : <p>No questions in this conversation yet.</p>}
                    <Button variant="secondary" onClick={startNewThread} disabled={busy}>New conversation</Button>
                  </div>
                </details>
                <details className="assistant-tool-section">
                  <summary>Saved prompts</summary>
                  <div className="assistant-saved-prompts">
                    {skills.length ? skills.map((skill) => <Button key={skill.id} variant="ghost" onClick={() => { updateInput(skill.prompt); setOptionsOpen(false); }}>{skill.name}</Button>) : <p>No saved prompts yet.</p>}
                    {latestUserMessage && <Button variant="secondary" onClick={saveLatestAsSkill} disabled={skillSaved}>{skillSaved ? "Saved" : "Save last question"}</Button>}
                    {skillError && <p role="alert">{skillError}</p>}
                  </div>
                </details>
                <details className="assistant-tool-section">
                  <summary>Usage</summary>
                  {usage?.monthlyUsed !== undefined && usage.monthlyLimit !== undefined ? <div className="assistant-usage"><span>This month</span><strong>{usage.monthlyUsed.toLocaleString()} / {usage.monthlyLimit.toLocaleString()} requests</strong>{usage.monthlyLimit > 0 && usage.monthlyUsed / usage.monthlyLimit >= 0.8 ? <em>{usage.monthlyUsed >= usage.monthlyLimit ? "Allowance used" : "Allowance nearly used"}</em> : null}</div> : <p>{canUseAPI ? "No usage figures in this conversation yet." : "Usage isn't tracked in this local preview."}</p>}
                </details>
                <details className="assistant-tool-section">
                  <summary>Daily review</summary>
                  {!canUseAPI ? <p>Scheduled reviews aren't available in this local preview.</p> : <div className="assistant-daily-review">
                    {automationState === "loading" && <p role="status">Loading daily review...</p>}
                    {automationState === "ready" && <p role="status">{dailyReview ? dailyReview.enabled ? "Daily review is on." : "Daily review is paused." : "Daily review is off."}</p>}
                    {automationError && <p role="alert">{automationError}</p>}
                    {automationState === "error" && <Button variant="secondary" onClick={() => setAutomationReload((current) => current + 1)}>Try again</Button>}
                    {automationState === "ready" && (dailyReview ? <Button variant="secondary" onClick={() => void deleteAutomation(dailyReview)} loading={automationBusy} loadingLabel="Turning off...">Turn off</Button> : automationSetupOpen ? <div className="assistant-automation-setup" role="group" aria-label="Enable daily reseller review"><p>Review sales, older stock and listings needing attention each day. Each run uses one included assistant request and pauses if your allowance is used.</p><div><Button variant="ghost" onClick={() => setAutomationSetupOpen(false)} disabled={automationBusy}>Cancel</Button><Button variant="primary" onClick={() => void createAutomation()} loading={automationBusy} loadingLabel="Enabling...">Enable daily review</Button></div></div> : <Button variant="secondary" onClick={() => setAutomationSetupOpen(true)}>Set up daily review</Button>)}
                  </div>}
                </details>
                <details className="assistant-tool-section">
                  <summary>Scope</summary>
                  <div className="assistant-scope"><strong>{store.workspace?.name}</strong><p>{canUseAPI ? "Answers use this workspace. Changes need your confirmation." : "Local stock counts and summaries only. No marketplace changes."}</p>
                    {invocationContext?.recordIDs?.map((id) => { const record = recordIndex.get(id); return record ? <AssistantRecordLink key={id} record={record} onOpenRecord={onOpenRecord ? openRecord : undefined} /> : null; })}
                    {invocationContext && <Button variant="ghost" onClick={() => setInvocationContext(undefined)}>Use whole workspace</Button>}
                  </div>
                </details>
              </div>
            ) : (
              <div className="assistant-messages" role="log" aria-label="Conversation" aria-live="polite" aria-relevant="additions text">
                {messages.map((message) => <div className={"assistant-message assistant-message-" + message.role} key={message.id} tabIndex={-1} ref={(element) => { if (element) messageRefs.current.set(message.id, element); else messageRefs.current.delete(message.id); }}>
                  {message.role === "assistant" ? <AssistantReplyBlocks text={message.text} blocks={message.reply?.blocks} onOpenRecord={onOpenRecord ? openRecord : undefined} /> : <p>{message.text}</p>}
                  {Boolean(message.reply?.citations?.length) && <details className="assistant-citations"><summary>Sources</summary>{message.reply?.citations?.map((citation, index) => {
                    const records = Array.from(new Set(citation.recordIDs)).map((id) => recordIndex.get(id)).filter((record): record is AssistantRecord => Boolean(record));
                    return <div key={citation.label + "-" + index}>{records.length ? records.map((record) => <AssistantRecordLink key={record.kind + "-" + record.id} record={record} label={citation.label + ": " + record.title} onOpenRecord={onOpenRecord ? openRecord : undefined} />) : <small>{citation.label}</small>}</div>;
                  })}</details>}
                  {message.reply?.action && <div className="assistant-action"><strong>{message.reply.action.title}</strong><span>{message.reply.action.summary} Nothing changes until you confirm.</span><Button size="compact" variant="primary" onClick={() => void sendMessage(message.source ?? message.text, true, message.reply?.action)} loading={busy} loadingLabel="Applying...">Confirm action</Button></div>}
                </div>)}
                {busy && <div className="assistant-message assistant-message-assistant assistant-message-working"><span>Checking your workspace...</span></div>}
                <div ref={messageEndRef} />
              </div>
            )}
          </div>
          <div className="assistant-composer-region">
            <form className="assistant-composer" onSubmit={(event) => { event.preventDefault(); void sendMessage(); }}>
              <input ref={inputRef} value={input} onChange={(event) => updateInput(event.target.value)} placeholder="Ask or describe an action..." aria-label="Ask the assistant" aria-describedby={storageWarning ? storageWarningID : undefined} />
              <Button type="submit" variant="primary" size="icon" disabled={!input.trim() || busy} aria-label="Send"><Send size={16} /></Button>
            </form>
            {storageWarning && <small className="assistant-storage-status" id={storageWarningID} role="status">Changes can't be saved on this device. Keep this tab open.</small>}
          </div>
        </section>
      )}
      <Button ref={launcherRef} className="assistant-launcher" variant="primary" onClick={() => open ? closeAssistant() : setOpen(true)} aria-expanded={open} aria-label={open ? "Close assistant" : "Open assistant"}><Bot size={18} /> <span>Ask assistant</span></Button>
    </div>
  );
}

async function requestAssistant(apiBase: string, workspaceID: string, message: string, confirm: boolean, messages: AssistantMessage[], context?: AssistantInvocationContext, action?: AssistantAction) {
  const response = await fetch(apiBase + "/api/workspaces/" + encodeURIComponent(workspaceID) + "/assistant", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message, confirm, action, context: { ...context, path: window.location.pathname, history: messages.slice(-8).map((entry) => ({ role: entry.role, text: entry.text, action: entry.role === "assistant" ? entry.reply?.action : undefined })) } }) });
  const body = await response.json() as { error?: string; reply: AssistantReply; database?: ReOSDatabase };
  if (!response.ok) throw new Error(body.error ?? "The assistant request failed.");
  return body;
}

export function supersedePendingAssistantActions(messages: AssistantMessage[]) {
  return messages.map((message) => message.reply?.action
    ? { ...message, reply: { ...message.reply, action: undefined } }
    : message);
}

function AssistantRecordLink({ record, label, onOpenRecord }: { record: AssistantRecord; label?: string; onOpenRecord?: AssistantPanelProps["onOpenRecord"] }) {
  const content = label ?? <><span className={"assistant-record-kind " + record.kind}>{record.kind.slice(0, 1).toUpperCase()}</span><span><b>{record.title}</b>{record.meta && <small>{record.meta}</small>}</span></>;
  return onOpenRecord ? <Button className="assistant-record-link" variant="ghost" onClick={() => onOpenRecord(record)}>{content}</Button> : <div className="assistant-record-row">{content}</div>;
}

function AssistantReplyBlocks({ text, blocks, onOpenRecord }: { text: string; blocks?: AssistantBlock[]; onOpenRecord?: AssistantPanelProps["onOpenRecord"] }) {
  if (!blocks?.length) return <AssistantRichText text={text} />;
  return <div className="assistant-response-blocks">{blocks.map((block, index) => {
    if (block.type === "markdown") return <AssistantRichText key={"markdown-" + index} text={block.content} />;
    if (block.type === "metrics") return <div className="assistant-metrics" key={"metrics-" + index}>{block.items.map((metric) => <div key={metric.label}><span>{metric.label}</span><strong>{metric.value}</strong>{metric.detail && <small>{metric.detail}</small>}</div>)}</div>;
    if (block.type === "records") return <div className="assistant-records" key={"records-" + index}><strong>{block.title}</strong>{block.records.map((record) => <AssistantRecordLink key={record.kind + "-" + record.id} record={record} onOpenRecord={onOpenRecord} />)}</div>;
    return <div className="assistant-checklist" key={"checklist-" + index}><strong>{block.title}</strong>{block.items.map((item) => <div key={item.label} data-status={item.status}><span aria-hidden="true" />{item.label}</div>)}</div>;
  })}</div>;
}

function assistantRecordIndex(database: ReOSDatabase, workspaceID: string, messages: AssistantMessage[]) {
  const records = new Map<string, AssistantRecord>();
  for (const message of messages) {
    for (const block of message.reply?.blocks ?? []) {
      if (block.type === "records") for (const record of block.records) records.set(record.id, record);
    }
  }
  for (const item of database.inventoryItems) if (item.workspaceID === workspaceID) records.set(item.id, { id: item.id, kind: "inventory", title: item.title, meta: item.sku });
  for (const listing of database.marketplaceListings) if (listing.workspaceID === workspaceID) records.set(listing.id, { id: listing.id, kind: "listing", title: listing.title });
  const items = new Map(database.inventoryItems.filter((item) => item.workspaceID === workspaceID).map((item) => [item.id, item]));
  for (const sale of database.sales) if (sale.workspaceID === workspaceID) records.set(sale.id, { id: sale.id, kind: "order", title: items.get(sale.itemID)?.title ?? "Order" });
  return records;
}

function welcomeMessages(): AssistantMessage[] {
  return [{ id: "assistant-welcome", role: "assistant", text: "Ask about your stock, listings, orders or what to do next." }];
}

function loadAssistantThread(scope: string): AssistantMessage[] {
  try {
    const stored = JSON.parse(localStorage.getItem("listwork:assistant:thread:v2:" + scope) ?? "null") as { version?: number; messages?: AssistantMessage[] } | null;
    if (stored?.version === 2 && Array.isArray(stored.messages) && stored.messages.length && stored.messages.every((message) => message && typeof message.id === "string" && typeof message.text === "string" && (message.role === "user" || message.role === "assistant"))) return stored.messages.slice(-60);
  } catch {
    // Unscoped legacy conversations are not migrated between account identities.
  }
  return welcomeMessages();
}

function saveAssistantThread(scope: string, messages: AssistantMessage[]) {
  try {
    localStorage.setItem("listwork:assistant:thread:v2:" + scope, JSON.stringify({ version: 2, messages: messages.slice(-60) }));
    return true;
  } catch {
    return false;
  }
}

function loadAssistantDraft(scope: string) {
  try { return localStorage.getItem("listwork:assistant:draft:v1:" + scope) ?? ""; }
  catch { return ""; }
}

function saveAssistantDraft(scope: string, draft: string) {
  try {
    const key = "listwork:assistant:draft:v1:" + scope;
    if (draft) localStorage.setItem(key, draft); else localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function localReply(database: ReOSDatabase, message: string): AssistantReply {
  const normalized = message.toLowerCase();
  const items = database.inventoryItems.filter((item) => item.workspaceID === database.activeWorkspaceID && !["sold", "archived"].includes(item.status));
  const aging = items.filter((item) => (inventoryAgeDays(item) ?? -1) >= 60);
  if (/\b(?:change|update|reduce|increase|delete|publish|apply|draft)\b/.test(normalized)) return { text: "This local preview can't draft or apply changes. I can show stock counts, older items and a business summary." };
  if (/\b(?:how many|count|number of)\b/.test(normalized) && /\b(?:inventory|items?|stock)\b/.test(normalized)) return { text: "You have " + items.length + " active inventory item" + (items.length === 1 ? "" : "s") + "." };
  if (/\boldest\b/.test(normalized) && /\b(?:inventory|items?|stock)\b/.test(normalized)) {
    const oldest = items.filter((item) => inventoryAgeDays(item) !== undefined).sort((a, b) => (inventoryAgeDays(b) ?? -1) - (inventoryAgeDays(a) ?? -1))[0];
    return { text: oldest ? "Your oldest active inventory item is **" + oldest.title + "** (SKU **" + oldest.sku + "**): " + inventoryAgeLabel(oldest) + "." : items.length ? "Stock age is unknown. Add acquisition dates to review the oldest items." : "There are no active inventory items to review." };
  }
  if (normalized.includes("stock") || normalized.includes("inventory") || normalized.includes("aging")) return { text: aging.length ? aging.length + " items need attention because they are more than 60 days old: " + aging.slice(0, 5).map((item) => item.title).join(", ") + "." : "No stock is currently over the 60-day attention threshold.", suggestions: ["Give me today's business summary", "What is my oldest inventory item?"] };
  if (normalized.includes("summary") || normalized.includes("today")) return { text: "You have " + items.length + " active inventory item" + (items.length === 1 ? "" : "s") + ", " + database.marketplaceListings.filter((listing) => listing.workspaceID === database.activeWorkspaceID && listing.status === "live").length + " live listings, and " + database.sales.filter((sale) => sale.workspaceID === database.activeWorkspaceID).length + " recorded sales." };
  return { text: "In this local preview I can show stock counts, older items and a business summary. Full conversations and changes need a connected workspace." };
}
